// Puzzle Studio building blocks: badges, the engine status line, the usage meter, the kill switch,
// callouts for the Studio error codes, verification details and the review buttons.

import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { canonicalAnswer } from "@lumen/puzzle-render";
import { useConfig } from "../../api/queries";
import type { AnswerValue, PuzzleStatus, VerificationResult, VerificationSummary } from "../../api/schemas";
import { useReview, useSetAiEnabled, useStudioTypes, useStudioUsage } from "../../api/studio";
import type { JobKind, JobStatus, StudioType, StudioUsage } from "../../api/studioSchemas";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, Loading, Select, Stat } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtCompact, fmtInt, formatAnswer } from "../../lib/format";
import {
  approvalBlocker,
  errorInfo,
  fmtUsd,
  JOB_STATUS_TONE,
  KIND_LABEL,
  monthLabel,
  newComponents,
  recentMonths,
  type Badge,
} from "../../lib/studio";
import pages from "../pages.module.css";
import s from "./studio.module.css";

// ---------------------------------------------------------------- chips

export function VerificationChip({ badge }: { badge: Badge }) {
  return (
    <span data-badge={badge.state} style={{ display: "inline-flex" }}>
      <Chip tone={badge.tone} title={badge.title}>
        {badge.label}
      </Chip>
    </span>
  );
}

export function JobStatusChip({ status, cancelRequested }: { status: JobStatus; cancelRequested?: boolean }) {
  const label = status === "running" && cancelRequested ? "cancelling" : status;
  return (
    <span data-job-status={status} style={{ display: "inline-flex" }}>
      <Chip tone={JOB_STATUS_TONE[status]}>
        {status === "running" || status === "queued" ? <span className={`${s.liveDot} ${status === "running" ? s.pulse : ""}`} aria-hidden /> : null}
        {label}
      </Chip>
    </span>
  );
}

export function KindChip({ kind }: { kind: JobKind }) {
  return <Chip tone={kind === "ai" ? "violet" : kind === "import" ? "muted" : kind === "levelpack" ? "accent" : "info"}>{KIND_LABEL[kind] ?? kind}</Chip>;
}

export function SourceChip({ source }: { source: string | undefined }) {
  if (!source) return null;
  const tone = source === "ai" ? "violet" : source === "curated" ? "teams" : source === "manual" ? "warning" : "info";
  return <Chip tone={tone}>{source === "ai" ? "Claude" : source}</Chip>;
}

/** Capability badges from the engine catalogue: solver, procedural, AI-supported, new component. */
export function CapabilityBadges({ type }: { type: StudioType }) {
  const missing = newComponents(type);
  return (
    <span className={s.badges}>
      {type.hasSolver ? (
        <Chip tone="success" title="Every puzzle must pass the solver's uniqueness check.">
          solver
        </Chip>
      ) : (
        <Chip tone="warning" title="No solver: a person approves every puzzle before it can be scheduled.">
          review
        </Chip>
      )}
      {type.procedural ? (
        <Chip tone="info" title="The iqcontent generator can make these without Claude.">
          procedural
        </Chip>
      ) : null}
      {type.aiSupported ? (
        <Chip tone="violet" title={type.promptVersion ? `Prompt ${type.promptVersion}` : "Claude drafts"}>
          {type.aiMode === "dedicated" ? "AI · own prompt" : "AI"}
        </Chip>
      ) : null}
      {missing.length ? (
        <Chip tone="danger" title={`Not in the web renderer yet: ${missing.join(", ")}. It can't ship server-only.`}>
          new component
        </Chip>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------- callouts

export function Callout({ tone = "info", title, code, children, testId }: { tone?: "info" | "warning" | "danger" | "success"; title: ReactNode; code?: string; children?: ReactNode; testId?: string }) {
  return (
    <div className={s.callout} data-tone={tone} role={tone === "danger" ? "alert" : "note"} data-testid={testId}>
      <div className={s.calloutHead}>
        <span className={s.calloutTitle}>{title}</span>
        {code ? <span className="monoLabel">{code}</span> : null}
      </div>
      {children}
    </div>
  );
}

/** An API error in plain words: the Studio codes (engine_unavailable, ai_disabled, budget_exceeded, not_verified…) get a title and a hint. */
export function StudioError({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  if (!error) return null;
  const info = errorInfo(error);
  return (
    <div className={s.callout} data-tone="danger" role="alert" data-code={info.code} data-testid="studio-error">
      <div className={s.calloutHead}>
        <span className={s.calloutTitle}>{title ?? info.title}</span>
        <span className="monoLabel">
          {info.code}
          {info.status ? ` · HTTP ${info.status}` : ""}
        </span>
      </div>
      <p>{info.message}</p>
      {info.hint ? <p className={s.calloutHint}>{info.hint}</p> : null}
      {onRetry ? (
        <div>
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- engine status

/** Engine reachable · Claude key · kill switch · default model. */
export function EngineStatus() {
  const types = useStudioTypes();
  const config = useConfig();
  const defaultModel = config.data?.values["studio.defaultModel"];
  const reachable = types.isSuccess;
  const down = types.isError;
  const ai = types.data?.ai;
  return (
    <div className={s.statusLine} data-testid="engine-status" aria-live="polite">
      <span className={s.statusItem} data-engine={reachable ? "up" : down ? "down" : "checking"}>
        <span className={s.statusDot} data-state={reachable ? "ok" : down ? "down" : undefined} aria-hidden />
        Engine{" "}
        <strong>{reachable ? `reachable · ${types.data.types.length} types` : down ? `unreachable (${errorInfo(types.error).code})` : "checking…"}</strong>
      </span>
      <span className={s.statusItem} data-ai-key={ai ? String(ai.keyConfigured) : "unknown"}>
        <span className={s.statusDot} data-state={ai ? (ai.keyConfigured ? "ok" : "warn") : undefined} aria-hidden />
        Claude key <strong>{ai ? (ai.keyConfigured ? "present" : "missing") : "—"}</strong>
      </span>
      <span className={s.statusItem}>
        <span className={s.statusDot} data-state={ai?.enabled ? "on" : undefined} aria-hidden />
        AI drafts <strong>{ai ? (ai.enabled ? "on" : "off") : "—"}</strong>
      </span>
      <span className={s.statusItem}>
        Default model <strong className="mono">{typeof defaultModel === "string" ? defaultModel : "—"}</strong>
        {ai?.defaultModel && ai.defaultModel !== defaultModel ? <span className="mono">(engine {ai.defaultModel})</span> : null}
      </span>
      <span className={s.statusGap} />
      {down ? (
        <Button size="sm" variant="ghost" onClick={() => void types.refetch()}>
          Check again
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- usage and budget

export function UsageCard() {
  const months = recentMonths(6);
  const [month, setMonth] = useState<string>(months[0]!);
  const q = useStudioUsage(month === months[0] ? null : month);
  return (
    <Card
      title="Claude usage and budget"
      actions={
        <Select aria-label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
          {months.map((m, i) => (
            <option key={m} value={m}>
              {i === 0 ? `This month · ${monthLabel(m)}` : monthLabel(m)}
            </option>
          ))}
        </Select>
      }
    >
      {q.isPending ? <Loading rows={4} /> : q.isError ? <StudioError error={q.error} onRetry={() => void q.refetch()} /> : <UsageBody usage={q.data} />}
    </Card>
  );
}

function UsageBody({ usage }: { usage: StudioUsage }) {
  const share = usage.budgetUsd > 0 ? usage.costUsd / usage.budgetUsd : usage.costUsd > 0 ? 1 : 0;
  const level = share >= 1 ? "over" : share >= 0.75 ? "warn" : "ok";
  const totalIn = usage.tokens.input + usage.tokens.cacheRead + usage.tokens.cacheWrite;
  return (
    <div data-testid="usage">
      <div className={s.usageTop}>
        <div className={s.usageBig}>
          <span className={s.usageAmount} data-testid="usage-cost">
            {fmtUsd(usage.costUsd)}
          </span>
          <span className={s.usageOf}>of {fmtUsd(usage.budgetUsd)} budget</span>
        </div>
        <Stat label="Remaining" value={<span data-testid="usage-remaining">{fmtUsd(usage.remainingUsd)}</span>} sub={level === "over" ? "budget spent: AI jobs stop" : "hard stop per UTC month"} />
      </div>
      <div className={s.budgetTrack} role="meter" aria-label="Budget used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(1, share) * 100)}>
        <span className={s.budgetFill} data-level={level} style={{ width: `${Math.min(100, share * 100)}%` }} />
      </div>
      <div className={s.budgetScale}>
        <span>$0</span>
        <span>{Math.round(share * 100)}% used</span>
        <span>{fmtUsd(usage.budgetUsd)}</span>
      </div>
      <div className={s.usageStats}>
        <Stat label="Input tokens" value={fmtCompact(totalIn)} sub={`${fmtCompact(usage.tokens.cacheRead)} cache reads`} />
        <Stat label="Output tokens" value={fmtCompact(usage.tokens.output)} />
        <Stat label="Claude calls" value={fmtInt(usage.calls)} />
        <Stat label="AI jobs" value={fmtInt(usage.aiJobs)} />
      </div>
      {usage.byModel.length ? (
        <table className={s.modelTable} data-testid="usage-models">
          <thead>
            <tr>
              <th>Model</th>
              <th data-align="right">Calls</th>
              <th data-align="right">Input</th>
              <th data-align="right">Output</th>
              <th data-align="right">Cost</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            {usage.byModel.map((m) => (
              <tr key={m.model}>
                <td className="mono">{m.model}</td>
                <td data-align="right">{fmtInt(m.calls)}</td>
                <td data-align="right">{fmtCompact(m.tokens.input + m.tokens.cacheRead + m.tokens.cacheWrite)}</td>
                <td data-align="right">{fmtCompact(m.tokens.output)}</td>
                <td data-align="right">{fmtUsd(m.costUsd)}</td>
                <td>
                  <span className={s.shareBar}>
                    <span style={{ width: `${usage.costUsd > 0 ? (m.costUsd / usage.costUsd) * 100 : 0}%` }} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={s.notes} style={{ marginTop: 16 }}>
          No Claude calls in {monthLabel(usage.month)}.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- kill switch

/** studio.aiEnabled: admins flip it after a confirm step; editors and viewers see it read-only. */
export function KillSwitch() {
  const usage = useStudioUsage();
  const types = useStudioTypes();
  const canToggle = useCan("studio.killSwitch");
  const { admin } = useAuth();
  const mutation = useSetAiEnabled();
  const [target, setTarget] = useState<boolean | null>(null);
  const enabled = usage.data?.aiEnabled ?? types.data?.ai.enabled ?? null;

  return (
    <Card title="AI kill switch" actions={enabled === null ? null : <Chip tone={enabled ? "accent" : "muted"}>{enabled ? "on" : "off"}</Chip>}>
      <div className={s.switchRow}>
        <button
          type="button"
          role="switch"
          className={s.switch}
          aria-checked={enabled ?? false}
          aria-label="Claude drafts (studio.aiEnabled)"
          disabled={!canToggle || enabled === null}
          title={canToggle ? undefined : "Only admins can change the kill switch"}
          onClick={() => {
            mutation.reset();
            setTarget(!enabled);
          }}
          data-testid="kill-switch"
        >
          <span className={s.switchKnob} />
        </button>
        <div className={s.switchText}>
          <strong>{enabled === null ? "Checking…" : enabled ? "Claude can draft puzzles" : "Claude drafts are off"}</strong>
          <p>
            New AI jobs need it. Turning it off stops running AI jobs at their next batch; procedural jobs carry on. Spend is capped by
            the monthly budget either way.
          </p>
        </div>
      </div>
      <div className={s.cardFoot}>
        {canToggle ? (
          <span className="monoLabel">studio.aiEnabled · admin</span>
        ) : (
          <span className={pages.readOnly} data-testid="kill-switch-read-only">
            Read-only · the kill switch needs the admin role (you are {admin?.role})
          </span>
        )}
        {types.data && !types.data.ai.keyConfigured ? <Chip tone="warning">no API key on the engine</Chip> : null}
      </div>
      <ConfirmDialog
        open={target !== null}
        title={target ? "Turn Claude drafts on?" : "Turn Claude drafts off?"}
        confirmLabel={target ? "Turn on" : "Turn off"}
        tone={target ? "primary" : "danger"}
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setTarget(null)}
        onConfirm={() => target !== null && mutation.mutate(target, { onSuccess: () => setTarget(null) })}
      >
        <p>
          Sets <code>studio.aiEnabled</code> to <strong>{String(target)}</strong>. The change is written to the audit log.
        </p>
        <p className={pages.faint}>
          {target
            ? `New Claude jobs can be queued. Each batch is sized to fit what is left of the monthly budget (${fmtUsd(usage.data?.remainingUsd)}).`
            : "New Claude jobs are refused (409 ai_disabled) and running ones fail at their next batch. Procedural jobs are not affected."}
        </p>
      </ConfirmDialog>
    </Card>
  );
}

// ---------------------------------------------------------------- verification

export function IssuesList({
  verification,
  max,
  hideManualNote,
}: {
  verification: Pick<VerificationResult, "issues" | "warnings"> | null | undefined;
  max?: number;
  /** Drop the engine's "no solver…" warning where a needs-review badge already says it. */
  hideManualNote?: boolean;
}) {
  const issues = verification?.issues ?? [];
  const warnings = (verification?.warnings ?? []).filter((w) => !hideManualNote || !w.startsWith("no solver for this type"));
  if (!issues.length && !warnings.length) return null;
  const all = [...issues.map((t) => ({ t, kind: "issue" })), ...warnings.map((t) => ({ t, kind: "warning" }))];
  const shown = max ? all.slice(0, max) : all;
  return (
    <ul className={s.issues} data-testid="issues">
      {shown.map((x, i) => (
        <li key={i} data-kind={x.kind}>
          {x.t}
        </li>
      ))}
      {shown.length < all.length ? <li data-kind="more">+{all.length - shown.length} more</li> : null}
    </ul>
  );
}

/** The full verification: method, result, uniqueness, score, who ran it, issues, warnings and notes. */
export function VerificationDetails({ v }: { v: VerificationResult | null | undefined }) {
  if (!v) return <p className={s.notes}>No stored verification. Generator rows were verified by the content build; others need Re-verify.</p>;
  return (
    <div className={s.section} data-testid="verification">
      <dl className={pages.kv}>
        <dt className="monoLabel">method</dt>
        <dd>{v.method === "solver" ? "solver (uniqueness check)" : "manual (a person approves)"}</dd>
        <dt className="monoLabel">result</dt>
        <dd>{v.ok ? "ok" : "failed"}{v.unique === true ? " · unique" : v.unique === false ? " · not unique" : ""}</dd>
        {v.difficultyScore !== undefined && v.difficultyScore !== null ? (
          <>
            <dt className="monoLabel">score</dt>
            <dd className="mono">{v.difficultyScore.toFixed(2)}</dd>
          </>
        ) : null}
        {v.by || v.at ? (
          <>
            <dt className="monoLabel">by</dt>
            <dd>
              {v.by ?? "—"}
              {v.at ? ` · ${formatDateTime(v.at)}` : ""}
            </dd>
          </>
        ) : null}
      </dl>
      <IssuesList verification={v} />
      {v.notes ? <p className={s.notes}>{v.notes}</p> : null}
    </div>
  );
}

export function AnswerText({ answer }: { answer: AnswerValue | null | undefined }) {
  if (!answer) return <span className={pages.faint}>no answer</span>;
  return (
    <span className={pages.answerValue} data-testid="answer">
      <span className="mono">{answer.kind}</span>
      <code title={canonicalAnswer(answer)}>{formatAnswer(answer)}</code>
    </span>
  );
}

// ---------------------------------------------------------------- review

const REVIEW: Record<"approved" | "rejected" | "draft", { label: string; tone: "primary" | "danger" | "secondary"; note: string }> = {
  approved: {
    label: "Approve",
    tone: "primary",
    note: "Approving a puzzle without a solver is its review. It still has to be scheduled to reach players.",
  },
  rejected: { label: "Reject", tone: "danger", note: "Rejected puzzles stay in the bank and can't be scheduled." },
  draft: { label: "Back to draft", tone: "secondary", note: "A puzzle that needs review goes back to pending." },
};

/** Approve / Reject (and Back to draft) through PATCH /puzzles/:id {status}, with a confirm step. */
export function ReviewActions({
  puzzle,
  size = "sm",
}: {
  puzzle: { id: string; status: PuzzleStatus; verification?: VerificationSummary | VerificationResult | null };
  size?: "sm" | "md";
}) {
  const canEdit = useCan("studio.edit");
  const mutation = useReview();
  const [pending, setPending] = useState<"approved" | "rejected" | "draft" | null>(null);
  if (!canEdit) return null;
  const targets: ("approved" | "rejected" | "draft")[] = puzzle.status === "draft" ? ["approved", "rejected"] : puzzle.status === "approved" ? ["draft", "rejected"] : [];
  const blocked = approvalBlocker(puzzle.verification);
  return (
    <>
      {targets.map((t) => (
        <Button
          key={t}
          size={size}
          variant={REVIEW[t].tone}
          disabled={t === "approved" && Boolean(blocked)}
          title={t === "approved" && blocked ? `${blocked} Fix it with Edit spec, or reject it.` : undefined}
          onClick={() => {
            mutation.reset();
            setPending(t);
          }}
        >
          {REVIEW[t].label}
        </Button>
      ))}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? `${REVIEW[pending].label} ${puzzle.id}?` : ""}
        confirmLabel={pending ? REVIEW[pending].label : "Confirm"}
        tone={pending === "rejected" ? "danger" : "primary"}
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && mutation.mutate({ id: puzzle.id, status: pending }, { onSuccess: () => setPending(null) })}
      >
        <p>
          <code>{puzzle.id}</code>: <strong>{puzzle.status}</strong> → <strong>{pending}</strong>.
        </p>
        <p className={pages.faint}>{pending ? REVIEW[pending].note : ""}</p>
      </ConfirmDialog>
    </>
  );
}

export function RoleNote() {
  const canEdit = useCan("studio.edit");
  const { admin } = useAuth();
  if (canEdit) return null;
  return (
    <span className={pages.readOnly} data-testid="read-only">
      Read-only · Studio changes need the editor role (you are {admin?.role})
    </span>
  );
}

export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={pages.back}>
      ← {children}
    </Link>
  );
}
